import { useEffect, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { useApp } from './lib/store'
import type { Skin } from './lib/skin'
import { startSync } from './lib/sync'
import { HomeScreen } from './screens/Home'
import { HomeModernScreen } from './screens/HomeModern'
import { QuickSellScreen } from './screens/QuickSell'
import { TablesScreen } from './screens/Tables'
import { CustomersScreen } from './screens/Customers'
import { TakeawayScreen } from './screens/Takeaway'
import { OrderScreen } from './screens/Order'
import { OrderModernScreen } from './screens/OrderModern'
import { PayScreen } from './screens/Pay'
import { ExpenseScreen } from './screens/Expense'
import { ReportScreen } from './screens/Report'
import { SettingsScreen } from './screens/Settings'
import { BankSettingsScreen } from './screens/BankSettings'
import { PrinterSettingsScreen } from './screens/PrinterSettings'
import { ProductsScreen } from './screens/Products'
import { PromotionsScreen } from './screens/Promotions'
import { ShiftScreen } from './screens/Shift'
import { HistoryScreen } from './screens/History'
import { ShopSettingsScreen } from './screens/ShopSettings'
import { EInvoiceSettingsScreen } from './screens/EInvoiceSettings'
import { StaffScreen } from './screens/Staff'
import { MenuScanScreen } from './screens/MenuScan'
import { BillingScreen } from './screens/Billing'
import { LoginScreen } from './screens/Login'
import { UpdateBanner } from './components/UpdateBanner'
import { AppShell } from './components/AppShell'

/** Bộ Modern có khung ngoài; bộ Classic thì không, và không thêm một lớp thẻ thừa nào. */
function Shell({ skin, children }: { skin: Skin; children: ReactNode }) {
  return skin === 'modern' ? <AppShell>{children}</AppShell> : <>{children}</>
}

export function App() {
  const ready = useApp((s) => s.ready)
  const session = useApp((s) => s.session)
  const boot = useApp((s) => s.boot)
  const toast = useApp((s) => s.toast)
  const storageBroken = useApp((s) => s.storageBroken)
  const skin = useApp((s) => s.skin)

  useEffect(() => {
    void boot()
  }, [boot])

  useEffect(() => {
    if (!session) return
    return startSync()
  }, [session])

  // Chưa đọc xong dữ liệu trên máy thì để trống — chớp một cái là xong,
  // hiện vòng xoay tải chỉ làm màn hình giật.
  if (!ready) return <div className="screen" />

  return (
    <BrowserRouter>
      {storageBroken && (
        <div className="storage-warning" role="alert">
          Máy này không cho phép lưu dữ liệu, nên app <b>không bán được khi mất mạng</b>. Nếu đang
          dùng cửa sổ ẩn danh thì mở lại bằng cửa sổ thường.
        </div>
      )}

      {!session ? (
        <LoginScreen />
      ) : (
        <Shell skin={skin}>
          <Routes>
            <Route path="/" element={skin === 'modern' ? <HomeModernScreen /> : <HomeScreen />} />
            <Route path="/tables" element={<TablesScreen />} />
            {/* Quầy mang về có màn hình riêng ở bộ Modern. Bộ Classic vẫn dùng ô Mang về
                trên sơ đồ bàn như cũ, nên đường dẫn này đưa về đó. */}
            <Route
              path="/takeaway"
              element={skin === 'modern' ? <TakeawayScreen /> : <Navigate to="/tables" replace />}
            />
            {/* Gọi món: bộ Modern có màn hình hai khoang riêng — thực đơn bên trái, tờ
                bill của bàn bên phải. Bộ Classic giữ nguyên màn hình một cột như cũ. */}
            <Route
              path="/order/:orderId"
              element={skin === 'modern' ? <OrderModernScreen /> : <OrderScreen />}
            />
            <Route path="/pay/:orderId" element={<PayScreen />} />
            {/* Bán nhanh có màn hình riêng ở bộ Modern. Bộ Classic mở thẳng đơn mang về
                như cũ, nên đường dẫn này chỉ đưa về trang chủ. */}
            <Route
              path="/quick"
              element={skin === 'modern' ? <QuickSellScreen /> : <Navigate to="/" replace />}
            />
            <Route path="/expense" element={<ExpenseScreen />} />
            <Route path="/report" element={<ReportScreen />} />
            <Route path="/shift" element={<ShiftScreen />} />
            <Route path="/history" element={<HistoryScreen />} />
            <Route path="/settings" element={<SettingsScreen />} />
            <Route path="/settings/shop" element={<ShopSettingsScreen />} />
            <Route path="/settings/einvoice" element={<EInvoiceSettingsScreen />} />
            <Route path="/settings/staff" element={<StaffScreen />} />
            <Route path="/settings/bank" element={<BankSettingsScreen />} />
            <Route path="/settings/printer" element={<PrinterSettingsScreen />} />
            <Route path="/settings/products" element={<ProductsScreen />} />
            <Route path="/settings/products/scan" element={<MenuScanScreen />} />
            <Route path="/settings/promotions" element={<PromotionsScreen />} />
            <Route path="/settings/billing" element={<BillingScreen />} />
            <Route path="/customers" element={<CustomersScreen />} />
            {/* Đường cũ từ hồi sổ nợ còn nằm trong Cài đặt. Giữ lại cho ai đã lưu nó. */}
            <Route path="/settings/customers" element={<Navigate to="/customers" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Shell>
      )}

      <UpdateBanner />

      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
    </BrowserRouter>
  )
}
